"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { FaArrowRight, FaCar, FaEnvelope, FaLocationDot, FaPhone, FaTag, FaWrench } from "react-icons/fa6";
import { getCarBuys, getOffers } from "../../service";

const OfferCards = dynamic(() => import("@/components/offer/OfferCards"), { ssr: false });

const BOOKING_URL = "/werkstatt-service";

// Foto im Startbereich – liegt im Ordner "public". Am besten ein echtes Foto eurer Werkstatt.
const HERO_IMAGE = "/werkzeug.jpeg";
const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Badenheimer+Str.+4,+55576+Sprendlingen,+Deutschland";

const AREAS = [
  {
    icon: <FaWrench />,
    title: "Werkstatt",
    text: "Wartung und Reparatur – Termin online wählen, ohne Warteschleife.",
    link: "Termin buchen",
    href: BOOKING_URL,
    highlight: true,
  },
  {
    icon: <FaCar />,
    title: "An- & Verkauf",
    text: "Unsere aktuellen Fahrzeuge mit allen Bildern und Details – oder verkaufen Sie uns Ihr Auto.",
    link: "Zum Bestand",
    href: "/fahrzeugkaufen",
  },
  {
    icon: <FaTag />,
    title: "Angebote",
    text: "Aktuelle Aktionen zu Service und Wartung – solange sie gelten.",
    link: "Alle Angebote",
    href: "#angebote",
  },
];

const STEPS = [
  { title: "Termin wählen", text: "Leistung, Tag und Uhrzeit aussuchen – freie Zeiten sehen Sie sofort." },
  { title: "Bestätigung erhalten", text: "Sie bekommen direkt eine E-Mail mit allen Details und einem Link zum Stornieren." },
  { title: "Auto vorbeibringen", text: "Zur vereinbarten Zeit kommen – wir kümmern uns um den Rest." },
];

// Server-Antwort in eine Liste umwandeln – egal ob [..] oder { cars: [..] } o. ä.
const toList = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  const firstArray = data && Object.values(data).find((v) => Array.isArray(v));
  return (firstArray as any[]) || [];
};

// Erstzulassung als "03/2019"
const formatRegistration = (v: unknown) => {
  if (!v) return null;
  const d = new Date(String(v));
  if (isNaN(d.getTime())) return String(v);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// Die wichtigsten Felder eines Autos (Feldnamen wie im Backend)
const toCarPreview = (car: any) => ({
  id: car._id,
  image: car.carImages?.[0] || "",
  title: car.carTitle || "Fahrzeug",
  price: car.carPrice,
  km: car.carKilometers,
  year: formatRegistration(car.carFirstRegistrationDay),
});

// Lädt Angebote und Fahrzeuge – "null" = lädt noch
const useHomeData = () => {
  const [offerCount, setOfferCount] = useState<number | null>(null);
  const [cars, setCars] = useState<ReturnType<typeof toCarPreview>[] | null>(null);

  useEffect(() => {
    getOffers()
      .then((res) => setOfferCount(toList(res.data).length))
      .catch(() => setOfferCount(0));

    getCarBuys()
      .then((res) => {
        const list = toList(res.data)
          .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
          .slice(0, 3)
          .map(toCarPreview);
        setCars(list);
      })
      .catch(() => setCars([]));
  }, []);

  return { hasOffers: !!offerCount, cars: cars || [] };
};

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className="text-xs font-semibold tracking-[0.18em] text-ao-accent-fg lg:text-[13px]">{children}</span>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="font-display text-4xl font-extrabold uppercase leading-none lg:text-6xl">{children}</h2>
);

export default function Home() {
  const { hasOffers, cars } = useHomeData();
  // Karte "Angebote" nur zeigen, wenn es Angebote gibt
  const areas = AREAS.filter((area) => area.title !== "Angebote" || hasOffers);

  return (
    <div className="bg-ao-bg text-ao-fg">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-20 px-5 pb-24 pt-10 lg:gap-32 lg:px-20 lg:pb-32 lg:pt-20">

        {/* ───── Startbereich ───── */}
        <section className="grid items-center gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
          <div className="flex flex-col gap-6 lg:gap-7">
            <div className="flex items-center gap-2.5">
              <span className="h-0.5 w-7 bg-ao-accent" />
              <Eyebrow>WERKSTATT · AN- &amp; VERKAUF</Eyebrow>
            </div>
            <h1 className="font-display text-[56px] font-extrabold uppercase leading-[0.92] sm:text-7xl lg:text-[104px]">
              Ihr Auto.
              <br />
              <span className="text-ao-accent-fg">In besten Händen.</span>
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-ao-muted lg:text-[19px]">
              Wartung, Reparatur und geprüfte Fahrzeuge – alles bei einem Ansprechpartner in Sprendlingen.
              Termin online wählen, Bestätigung direkt per E-Mail.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                href={BOOKING_URL}
                className="flex h-14 items-center justify-center gap-2.5 rounded-xl bg-ao-accent px-7 text-base font-bold text-ao-on-accent transition-colors hover:bg-ao-accent-hover"
              >
                Termin online buchen <FaArrowRight className="text-sm" />
              </Link>
              <Link
                href="/fahrzeugkaufen"
                className="flex h-14 items-center justify-center rounded-xl border border-ao-line-strong px-7 text-base font-semibold transition-colors hover:border-ao-accent"
              >
                Fahrzeuge ansehen
              </Link>
            </div>
          </div>

          {/* Foto mit Hinweis-Karte */}
          <div className="relative">
            <div className="aspect-[4/3] overflow-hidden rounded-3xl border border-ao-line bg-ao-surface-2 lg:aspect-[5/5.2]">
              <img
                src={HERO_IMAGE}
                alt="Werkstatt von A und O Autoservice"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 left-4 right-4 flex items-center gap-4 rounded-2xl border border-ao-line bg-ao-surface p-4 shadow-2xl sm:right-auto sm:w-[320px] lg:-left-8 lg:bottom-10">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ao-surface-2 text-ao-accent-fg">
                <FaEnvelope />
              </span>
              <span className="flex flex-col gap-0.5">
                <span className="text-[15px] font-bold">Sofort bestätigt</span>
                <span className="text-[13px] leading-snug text-ao-muted">
                  Terminbestätigung direkt per E-Mail – mit Link zum Stornieren.
                </span>
              </span>
            </div>
          </div>
        </section>

        {/* ───── Kontakt-Leiste ───── */}
        <section className="-mt-6 grid gap-2 rounded-2xl border border-ao-line bg-ao-surface p-2 md:grid-cols-3 lg:-mt-16">
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 rounded-xl p-4 transition-colors hover:bg-ao-surface-2"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ao-surface-2 text-ao-accent-fg">
              <FaLocationDot />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-[13px] text-ao-muted">Route planen</span>
              <span className="text-[15px] font-semibold">Badenheimer Str. 4, Sprendlingen</span>
            </span>
          </a>
          <a href="tel:+4915158124394" className="flex items-center gap-4 rounded-xl p-4 transition-colors hover:bg-ao-surface-2">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ao-surface-2 text-ao-accent-fg">
              <FaPhone />
            </span>
            <span className="flex flex-col">
              <span className="text-[13px] text-ao-muted">Anrufen</span>
              <span className="text-[15px] font-semibold">+49 151 58124394</span>
            </span>
          </a>
          <a href="mailto:autoservice.aundo@gmail.com" className="flex items-center gap-4 rounded-xl p-4 transition-colors hover:bg-ao-surface-2">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ao-surface-2 text-ao-accent-fg">
              <FaEnvelope />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-[13px] text-ao-muted">E-Mail schreiben</span>
              <span className="truncate text-[15px] font-semibold">autoservice.aundo@gmail.com</span>
            </span>
          </a>
        </section>

        {/* ───── Bereiche ───── */}
        <section className="flex flex-col gap-10 lg:gap-12">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
            <div className="flex flex-col gap-3">
              <Eyebrow>UNSERE BEREICHE</Eyebrow>
              <SectionTitle>Alles rund ums Auto</SectionTitle>
            </div>
            <p className="max-w-sm text-base leading-relaxed text-ao-muted">
              Ob Service oder Ihr nächstes Fahrzeug – bei uns aus einer Hand.
            </p>
          </div>
          <div className={`grid gap-4 lg:gap-5 ${areas.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
            {areas.map((area) => (
              <Link
                key={area.title}
                href={area.href}
                className="group flex min-h-[260px] flex-col gap-5 rounded-2xl border border-ao-line bg-ao-surface p-7 transition-colors hover:border-ao-accent"
              >
                <span
                  className={`flex h-[52px] w-[52px] items-center justify-center rounded-[14px] text-xl ${
                    area.highlight ? "bg-ao-accent text-ao-on-accent" : "bg-ao-surface-2 text-ao-accent-fg"
                  }`}
                >
                  {area.icon}
                </span>
                <span className="font-display text-[28px] font-bold uppercase leading-none">{area.title}</span>
                <span className="flex-1 text-[15px] leading-relaxed text-ao-muted">{area.text}</span>
                <span className="flex items-center gap-2 text-[15px] font-bold text-ao-accent-fg">
                  {area.link}
                  <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* ───── Angebote (nur wenn vorhanden) ───── */}
        {hasOffers && (
          <section id="angebote" className="flex scroll-mt-28 flex-col gap-10 lg:gap-12">
            <div className="flex flex-col gap-3">
              <Eyebrow>JETZT SPAREN</Eyebrow>
              <SectionTitle>Aktuelle Angebote</SectionTitle>
            </div>
            <OfferCards />
          </section>
        )}

        {/* ───── Fahrzeuge (nur wenn vorhanden) ───── */}
        {cars.length > 0 && (
          <section className="flex flex-col gap-10 lg:gap-12">
            <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
              <div className="flex flex-col gap-3">
                <Eyebrow>FAHRZEUGE</Eyebrow>
                <SectionTitle>Aktuell im Bestand</SectionTitle>
              </div>
              <Link href="/fahrzeugkaufen" className="flex items-center gap-2 text-[15px] font-bold text-ao-accent-fg">
                Alle Fahrzeuge ansehen <FaArrowRight className="text-xs" />
              </Link>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-5">
              {cars.map((car) => (
                <Link
                  key={car.id}
                  href={`/fahrzeugkaufen/${car.id}`}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-ao-line bg-ao-surface transition-colors hover:border-ao-accent"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-ao-surface-2">
                    {car.image ? (
                      <img
                        src={car.image}
                        alt={car.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-4xl text-ao-line-strong">
                        <FaCar />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col gap-4 p-6">
                    <h3 className="font-display text-2xl font-bold uppercase leading-tight">{car.title}</h3>
                    {(car.year || car.km) && (
                      <div className="flex flex-wrap gap-2">
                        {car.year && (
                          <span className="rounded-lg bg-ao-surface-2 px-3 py-1.5 text-[13px] text-ao-soft">{car.year}</span>
                        )}
                        {car.km != null && car.km !== "" && (
                          <span className="rounded-lg bg-ao-surface-2 px-3 py-1.5 text-[13px] text-ao-soft">
                            {Number(car.km).toLocaleString("de-DE")} km
                          </span>
                        )}
                      </div>
                    )}
                    <div className="mt-auto flex items-center justify-between border-t border-ao-line pt-4">
                      {car.price != null && car.price !== "" ? (
                        <span className="font-display text-3xl font-extrabold">
                          {Number(car.price).toLocaleString("de-DE")} €
                        </span>
                      ) : (
                        <span className="text-[15px] text-ao-muted">Preis auf Anfrage</span>
                      )}
                      <span className="flex items-center gap-2 text-[15px] font-bold text-ao-accent-fg">
                        Details <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ───── Ablauf ───── */}
        <section className="flex flex-col gap-10 lg:gap-12">
          <div className="flex flex-col items-center gap-3 text-center">
            <Eyebrow>SO EINFACH GEHT&apos;S</Eyebrow>
            <SectionTitle>In drei Schritten zum Termin</SectionTitle>
          </div>
          <ol className="grid gap-4 md:grid-cols-3 lg:gap-5">
            {STEPS.map((step, i) => (
              <li key={step.title} className="flex flex-col gap-3 rounded-2xl border border-ao-line p-7 lg:p-9">
                <span className="font-display text-6xl font-extrabold leading-none text-ao-accent-fg lg:text-7xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-xl font-bold lg:text-[22px]">{step.title}</span>
                <span className="text-[15px] leading-relaxed text-ao-muted">{step.text}</span>
              </li>
            ))}
          </ol>
          <div className="flex justify-center">
            <Link
              href={BOOKING_URL}
              className="flex h-14 items-center justify-center gap-2.5 rounded-xl bg-ao-accent px-8 text-base font-bold text-ao-on-accent transition-colors hover:bg-ao-accent-hover"
            >
              Jetzt Termin buchen <FaArrowRight className="text-sm" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
